import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PersonRow } from "../../lib/api";
import { typeaheadQuery } from "../../lib/queries";
import { parseKnockHandle } from "../../lib/knock-nav";
import { SearchIcon, XGlyph } from "../ui/icons";
import { Monogram } from "../ui/monogram";
import { DevDoors } from "./dev-doors";

/**
 * The way a door is found — X's people search, inside the knock dialog
 * (rulings R-0904-3, R-0905-2, R-0905-9).
 *
 * THREE ROADS, and the order between them is the whole design (ruling
 * R-0905-2 fixed the order for free text: Nob's index, then the address, then
 * X).
 *
 *   THE ADDRESS. You know the handle, you type it with its `@`, you go. No
 *   search, no round trip, no list to pick out of — the same as typing a
 *   street address, and the same road a shared `nob.me/@handle` link takes. A
 *   handle typed with its `@` never queries either search endpoint at all.
 *   This road MUST NOT REGRESS: an address somebody was handed has to work
 *   whatever the index knows, so the day it starts depending on search is the
 *   day handed-out links start failing for reasons nobody can see.
 *
 *   THE TYPEAHEAD. You know a name. Two characters and a quarter-second of
 *   quiet later, everybody Nob already knows who matches appears beneath the
 *   box — doors first, then the better-followed — each row wearing what Nob
 *   knows: a door open, a waiting room, or nothing. Free, on every keystroke.
 *
 *   THE ADDRESS, OFFERED. Nobody types the `@` (owner, 2026-09-05). When what
 *   is in the box is shaped like a handle, the list also offers "Knock at
 *   @<it>" — directly under Nob's own rows, above X — and that row IS the
 *   address road: the same `onGo`, no search endpoint asked, resolution at the
 *   door on selection. It is not drawn when Nob's rows already hold that exact
 *   handle, because one person twice is two people to a thumb.
 *
 *   X. The last row of the list is always "Search X for '…'", and it is a
 *   LINK — X's own people search, opened in a new tab (ruling R-0905-9). X
 *   bills about a cent per person its search returns, and the owner ruled
 *   that spend out of the product entirely: the row calls nothing of ours,
 *   and no request of any kind leaves this dialog on that road. Enter with no
 *   row picked opens the same link. The person comes back with the handle and
 *   takes the address row, or types it.
 *
 * Picking a person is one destination: the compose thread at that handle,
 * which is a knock where there is a door and the waiting room where there is
 * not. The dialog owns that road (`onGo`); this box only names the handle.
 *
 * NOB IS STILL NOT A DIRECTORY. There is no browse here, no listing of anything
 * nobody asked for: an empty box shows an empty room, and the server's own cap
 * is what the longest answer can be.
 */

/** How long the typing has to stop before it counts as a search. */
const DEBOUNCE_MS = 250;

/** Matches the server's floor: below this a query is a keystroke, not a search. */
const MIN_QUERY = 2;

export const NOTHING_FOUND =
  "Nobody Nob knows matches — knock at the handle if you know it, or search X.";

/** The offered address row: the handle in the box, taken as the address it is. */
export const KNOCK_AT = "Knock at";

/** X's own shape for a handle — and therefore the one the address row offers for. */
const HANDLE_SHAPED = /^[A-Za-z0-9_]{1,15}$/;

/** A search that FAILED is not an empty answer, and must never read as one. */
export const SEARCH_FAILED =
  "Search is not answering. Try that again — or type the @handle if you know it.";

/** The last row of the list: a link out to X's own people search. */
export const SEARCH_X = "Search X for";

/**
 * X's people search for a query, on x.com itself (ruling R-0905-9). `f=user`
 * is X's own "People" tab. Nothing of ours is asked on this road.
 */
export function xPeopleSearchUrl(query: string): string {
  return `https://x.com/search?q=${encodeURIComponent(query)}&f=user`;
}

/** The link, opened by the keyboard: a new tab, and no opener handed to x.com. */
function openXPeopleSearch(query: string): void {
  window.open(xPeopleSearchUrl(query), "_blank", "noopener,noreferrer");
}

export const BADGE_DOOR = "door open";
export const BADGE_WAITING = "waiting room";

export function DoorSearch({
  onGo,
  waiting,
  children,
}: {
  onGo: (handle: string) => void;
  /**
   * The queues, beneath the people.
   *
   * A render prop rather than a component this file imports, because the two
   * lists come from different places and must go on answering different
   * questions: this box asks the TYPEAHEAD who matches, and the caller asks
   * the BOARD which handles people are waiting at. Handing the caller the live
   * query text is the entire seam — nothing about a demand record reaches the
   * search endpoints, and nothing about a person reaches the board's.
   *
   * CALLED FOR A TYPED `@handle` TOO, and told which it is. That road ends at
   * one account rather than at a list, and what is at that account — a door, a
   * queue, or an X user who has never heard of this product — is a question
   * only the caller can ask, through the identity port, one handle at a time.
   * It is still not a search: no text reaches either search endpoint on this
   * road, and none reaches the board's.
   */
  waiting?: (query: string, isAddress: boolean) => ReactNode;
  /** What the room shows while nobody is searching. */
  children?: ReactNode;
}) {
  const [text, setText] = useState("");
  const [debounced, setDebounced] = useState("");
  /** The row the arrow keys are on: an index into the people, or the X row past them. */
  const [selected, setSelected] = useState<number | null>(null);

  const typed = text.trim();
  /**
   * A leading `@` is a statement: this is an address, not a name. Honouring it
   * means somebody walking to a door they were given never sends the handle to
   * a search endpoint — which matters most for the doors search would not
   * answer for anyway.
   */
  const isAddress = typed.startsWith("@");

  useEffect(() => {
    setSelected(null);
    if (isAddress) {
      setDebounced("");
      return;
    }
    const t = setTimeout(() => setDebounced(typed), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [typed, isAddress]);

  const query = debounced.trim();
  const searching = query.length >= MIN_QUERY;
  const {
    data: rows = [],
    isFetching,
    isError,
  } = useQuery({
    ...typeaheadQuery(query),
    enabled: searching,
  });

  /**
   * The address road, offered (ruling R-0905-2): a handle-shaped query is also
   * an address, so the list says so — unless Nob's own rows already carry that
   * exact handle, in which case that row is the address. Handles are compared
   * case-blind: the index stores them lowered.
   */
  const known = new Set(rows.map((row) => row.handle.toLowerCase()));
  const address = query.toLowerCase();
  const offerAddress = searching && HANDLE_SHAPED.test(query) && !known.has(address);

  /** The rows the arrow keys walk: the people, the address row, then the X row. */
  const addressAt = offerAddress ? rows.length : -1;
  const xAt = rows.length + (offerAddress ? 1 : 0);
  const count = searching ? xAt + 1 : 0;

  /**
   * What Enter means, in the order the roads rank. A typed `@handle` is an
   * address and wins over any list; a picked row is that row; and a name with
   * nothing picked opens X's people search — the last row of the list, taken
   * from the keyboard.
   */
  const submit = () => {
    if (isAddress) {
      const clean = parseKnockHandle(text);
      if (clean) onGo(clean);
      return;
    }
    if (!searching) return;
    if (selected !== null && selected < rows.length) {
      onGo(rows[selected]!.handle);
      return;
    }
    if (selected !== null && selected === addressAt) {
      onGo(address);
      return;
    }
    openXPeopleSearch(query);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isAddress || count === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((s) => (s === null ? 0 : Math.min(count - 1, s + 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((s) => (s === null || s === 0 ? null : s - 1));
    }
  };

  return (
    <div className="nob-enter flex flex-col gap-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label htmlFor="door-search" className="sr-only">
          Search doors — a name or @handle
        </label>
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted" />
          <input
            id="door-search"
            type="search"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="Search doors — a name or @handle"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            className="h-10 w-full rounded-full bg-search pr-3 pl-9 text-sm text-text
                       placeholder:text-fainter focus:outline-none focus:ring-2 focus:ring-focus/70"
          />
        </div>
      </form>

      {!searching && !isAddress && children}

      {/* The address road's own hint — and only where nobody else is drawing
          one. A caller that renders for `isAddress` has ASKED what is at that
          handle and can say something truer than "press enter": whether there
          is a door there at all. Two hints stacked, one of them a guess, is the
          state this condition exists to prevent. */}
      {isAddress && !waiting && (
        <p className="px-1 text-sm leading-relaxed text-faint">
          Press enter to knock at <span className="font-mono text-text-soft">{typed}</span>.
        </p>
      )}

      {searching && isError && !isFetching && (
        <p className="px-1 text-sm leading-relaxed text-faint" role="alert">
          {SEARCH_FAILED}
        </p>
      )}

      {/* Not drawn over an offered address row: the two rows beneath ARE the
          two roads this sentence names, and saying them twice read wrong on
          the rendered dialog (visual review, 2026-09-05). */}
      {searching && !isError && rows.length === 0 && !offerAddress && !isFetching && (
        <p className="px-1 text-sm leading-relaxed text-faint">{NOTHING_FOUND}</p>
      )}

      {searching && !isError && (
        <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
          {rows.map((row, index) => (
            <PersonItem
              key={row.handle}
              row={row}
              selected={selected === index}
              onClick={() => onGo(row.handle)}
            />
          ))}
          {offerAddress && (
            <li>
              <button
                type="button"
                onClick={() => onGo(address)}
                className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-left
                            transition-colors duration-75 hover:bg-hover
                            focus-visible:outline-none focus-visible:ring-2
                            focus-visible:ring-focus/70 focus-visible:ring-inset
                            ${selected === addressAt ? "bg-hover" : ""}`}
              >
                <span
                  aria-hidden
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full
                             bg-raised-strong font-mono text-base text-text"
                >
                  @
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-text">
                  {KNOCK_AT} <span className="font-mono font-semibold">@{address}</span>
                </span>
              </button>
            </li>
          )}
          {/* The last row, and a LINK rather than a button: it leaves for x.com
              in a new tab and asks nothing of ours (ruling R-0905-9). The X
              glyph and the last place are kept; what changed is where it goes. */}
          <li>
            <a
              href={xPeopleSearchUrl(query)}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-left
                          transition-colors duration-75 hover:bg-hover
                          focus-visible:outline-none focus-visible:ring-2
                          focus-visible:ring-focus/70 focus-visible:ring-inset
                          ${selected === xAt ? "bg-hover" : ""}`}
            >
              <span
                aria-hidden
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-raised-strong text-text"
              >
                <XGlyph className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-text">
                {SEARCH_X} <span className="font-semibold">&apos;{query}&apos;</span>
              </span>
            </a>
          </li>
        </ul>
      )}

      {waiting?.(isAddress ? typed : query, isAddress)}

      <DevDoors onGo={onGo} />
    </div>
  );
}

/**
 * One person, the way X's suggestion rows draw one: a face, a name, the
 * address, and a badge. The face is Nob's cached picture (our own origin,
 * FR-3.8.2) or a monogram — never a picture fetched from X, whichever list the
 * row came from. No follower count, no rank: the count ordered the list on the
 * server and stays there.
 */
function PersonItem({
  row,
  selected = false,
  onClick,
}: {
  row: PersonRow;
  selected?: boolean;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-left
                    transition-colors duration-75 hover:bg-hover
                    focus-visible:outline-none focus-visible:ring-2
                    focus-visible:ring-focus/70 focus-visible:ring-inset
                    ${selected ? "bg-hover" : ""}`}
      >
        {row.avatar ? (
          <img
            src={row.avatar}
            alt=""
            width={36}
            height={36}
            className="h-9 w-9 shrink-0 rounded-full bg-raised object-cover"
          />
        ) : (
          <Monogram name={row.display_name} handle={row.handle} />
        )}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-text">{row.display_name}</span>
          <span className="truncate font-mono text-xs text-faint">@{row.handle}</span>
        </span>
        {row.badge && (
          <span
            className="shrink-0 rounded-full border border-border/80 px-2 py-0.5 text-[10px]
                       font-medium uppercase tracking-[0.12em] text-muted"
          >
            {row.badge === "door" ? BADGE_DOOR : BADGE_WAITING}
          </span>
        )}
      </button>
    </li>
  );
}
